import { SasResponse } from '@/types';
import { apiClient } from './client';

/**
 * Cache for SAS tokens to avoid unnecessary API calls
 */
const sasTokenCache = new Map<
  string,
  { token: SasResponse; fetchedAt: number }
>();

/**
 * Cache duration in milliseconds (5 minutes less than token expiry to be safe)
 */
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Validate blob URL format
 */
function validateBlobUrl(url: string): void {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('.blob.core.windows.net')) {
      throw new Error('URL must be an Azure Blob Storage URL');
    }
  } catch {
    throw new Error('Invalid blob URL format');
  }
}

/**
 * Check if cached token is still valid
 */
function isCacheValid(cachedEntry: {
  token: SasResponse;
  fetchedAt: number;
}): boolean {
  const now = Date.now();
  const age = now - cachedEntry.fetchedAt;

  // Check if cache is not too old
  if (age > CACHE_DURATION) {
    return false;
  }

  // Check if token hasn't expired
  const expiryTime = new Date(cachedEntry.token.expiryTime).getTime();
  const timeUntilExpiry = expiryTime - now;

  // Consider invalid if less than 1 minute until expiry
  return timeUntilExpiry > 60000;
}

/**
 * SAS Token API service
 */
export const sasApi = {
  /**
   * Generate SAS token for blob URL
   */
  async generateToken(
    blobUrl: string,
    expiryMinutes = 15
  ): Promise<SasResponse> {
    // Validate URL format
    validateBlobUrl(blobUrl);

    // Check cache first
    const cacheKey = `${blobUrl}:${expiryMinutes}`;
    const cached = sasTokenCache.get(cacheKey);

    if (cached && isCacheValid(cached)) {
      return cached.token;
    }

    // Fetch new token
    const { data } = await apiClient.get<SasResponse>('/sas', {
      params: { blobUrl, expiryMinutes },
    });

    // Cache the token
    sasTokenCache.set(cacheKey, {
      token: data,
      fetchedAt: Date.now(),
    });

    return data;
  },

  /**
   * Clear the SAS token cache
   */
  clearCache(): void {
    sasTokenCache.clear();
  },

  /**
   * Remove specific URL from cache
   */
  removeCachedUrl(blobUrl: string): void {
    // Remove all entries for this URL regardless of expiry time
    const keysToDelete: string[] = [];
    sasTokenCache.forEach((_, key) => {
      if (key.startsWith(blobUrl)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => sasTokenCache.delete(key));
  },

  /**
   * Get cache size (for debugging/monitoring)
   */
  getCacheSize(): number {
    return sasTokenCache.size;
  },

  /**
   * Validate SAS token (check if it's still valid)
   */
  async validateToken(sasUrl: string): Promise<boolean> {
    try {
      const { data } = await apiClient.post<{ valid: boolean }>(
        '/sas/validate',
        {
          sasUrl,
        }
      );
      return data.valid;
    } catch {
      return false;
    }
  },
};
